import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router';
import { Helmet } from 'react-helmet';
import DOMPurify from 'dompurify';
import useApiUrl from '../../framework/hooks/useApiUrlHook';
import Navigation from '../../components/Navigation';
import EorikSlider from '../../components/slider/EorikSlider';
import Footer from '../../components/Footer';
import HotspotsMap from '../../components/maps/HotspotsMap';
import Image from '../../components/Image';

export default function HotspotOverview() {
    const apiUrl = useApiUrl();
    const [data, setData] = useState(false);
    const [sorting, setSorting] = useState(localStorage.getItem('sorting') ?? 'popular');
    const [sortings, setSortings] = useState({});
    const [geoLocation, setGeoLocation] = useState(false);
    const { location } = useParams();

    const getData = () => {
        axios
            .post(`${apiUrl}/hotspots/overview`, { location })
            .then((response) => {
                setData(response.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    useEffect(() => {
        if (apiUrl != null) getData();

        setSort(localStorage.getItem('sorting') ?? 'popular');

        if (data) {
            sort();
        }
    }, [apiUrl]);

    useEffect(() => {
        sort();
    }, [data, geoLocation, sorting]);

    function sort() {
        switch (sorting) {
            case 'popular':
                data?.hotspots?.sort((a, b) => {
                    return b.views - a.views;
                });
                break;
            case 'asc':
            case 'desc':
                data?.hotspots?.sort((a, b) => {
                    return sortNames(a.titel, b.titel, sorting === 'asc');
                });
                break;
            case 'nearby':
                if (geoLocation) {
                    data.hotspots?.forEach((a) => {
                        a.distance = calcCrow(geoLocation, a.lon, a.lat);
                    });
                    data?.hotspots?.sort((a, b) => {
                        // console.log(calcCrow(geoLocation, a.lon, a.lat), calcCrow(geoLocation, b.lon, b.lat))
                        return a.distance - b.distance;
                    });
                }
                break;
        }

        loadSortings();
    }

    function sortNames(a, b, asc) {
        const nameA = a.toUpperCase(); // ignore upper and lowercase
        const nameB = b.toUpperCase(); // ignore upper and lowercase

        if (asc) {
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }
        } else {
            if (nameA < nameB) {
                return 1;
            }
            if (nameA > nameB) {
                return -1;
            }
        }

        // names must be equal
        return 0;
    }

    function setSort(sort) {
        switch (sort) {
            case 'nearby':
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition((position) => {
                        setGeoLocation(position.coords);
                        setSorting(sort);
                        localStorage.setItem('sorting', sort);
                    });
                } else {
                }
                break;
            default:
                localStorage.setItem('sorting', sort);
                setSorting(sort);
                break;
        }
    }

    function loadSortings() {
        const s = {
            popular: { name: 'Populair' },
            asc: { name: 'A - Z' },
            desc: { name: 'Z - A' },
            nearby: { name: 'In de buurt' },
        };

        const selected = s[sorting];

        delete s[sorting];

        setSortings({ selected, other: s });
    }

    function calcCrow(geoLocation, lon2, lat2) {
        const lon1 = geoLocation.longitude;
        const lat1 = geoLocation.latitude;

        const R = 6371; // km
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const latR1 = toRad(lat1);
        const latR2 = toRad(lat2);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(latR1) * Math.cos(latR2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c;
        return d;
    }

    // Converts numeric degrees to radians
    function toRad(Value) {
        return (Value * Math.PI) / 180;
    }

    return (
        <>
            <Helmet>
                <title>{`${process.env.MIX_REACT_APP_APP_NAME} - Hotspots in ${data.location?.titel}`}</title>
            </Helmet>
            <Navigation />
            {/* Start Ecorik Slider Area */}
            <div className="banner-wrapper">
                <section className="eorik-slider-area">
                    <EorikSlider
                        items={data?.slideshow}
                        random
                        imageFolder="locaties"
                        imageTyoe="middel"
                        quality={50}
                        text={`Ontdek ${data?.location?.titel}`}
                    />
                    <img className="mobile-shape" src="/assets/images/banner/mobile-img.png" alt="mobile-img" />
                </section>
                <div className="white-shape">
                    <img src="/assets/images/banner/banner-shape.png" alt="Image" />
                </div>
            </div>
            {/* End Ecorik Slider Area */}
            {/* Start Wandelroute Area */}
            <div className="wandelroute-area pb-100">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6" data-aos="fade-up" data-aos-duration={1000}>
                            <div className="wandelroute-content">
                                <h2>Wandelroute langs bezienswaardigheden in {data?.location?.titel}</h2>
                                <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.location?.inleiding) }} />
                            </div>
                        </div>
                        <div className="col-lg-6" data-aos="fade-up" data-aos-duration={1000}>
                            <div className="google-apple">
                                <a href="/app/google-play" target="_blank" className="store-btn">
                                    <img src="/assets/images/icon/google.svg" alt="google" />
                                    <span>Download Google Play</span>
                                </a>
                                <a href="/app/apple-store" target="_blank" className="store-btn">
                                    <img src="/assets/images/icon/apple.svg" alt="apple" />
                                    <span>Download App Store</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* End Wandelroute Area */}
            {/* End Hotspots Area */}
            <div className="hotspots-area bg-color-f4f9ed pt-100 pb-70">
                <div className="container">
                    <div
                        className="section-title d-md-flex d-lg-flex justify-content-between align-items-center mw-100"
                        data-aos="fade-up"
                        data-aos-duration={1000}
                    >
                        <h2>
                            Bekijk hier alle {data?.hotspots?.length} hotspots in {data?.location?.titel}
                        </h2>
                        <nav className="nav">
                            <ul className="navigation">
                                <li>
                                    <a href="#;" className="d-flex justify-content-between align-items-center">
                                        <span>{sortings?.selected?.name} </span>
                                        <i className="ri-arrow-right-s-line" />
                                    </a>
                                    <ul>
                                        {sortings.other &&
                                            Object.keys(sortings?.other).map((key, index) => (
                                                <li key={index}>
                                                    <a
                                                        className="buurt"
                                                        href="#"
                                                        onClick={(e) => {
                                                            setSort(key);
                                                            e.preventDefault();
                                                        }}
                                                    >
                                                        {sortings.other[key].name}
                                                    </a>
                                                </li>
                                            ))}
                                    </ul>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <div className="row justify-content-center">
                        {data?.hotspots?.map((item) => (
                            <a key={item.id} href={`/hotspots/${data.location?.url}/${item.url}`} className="col">
                                <div className="single-bekijeck text-center">
                                    <Image
                                        className="lazy"
                                        src={
                                            item.images?.[0]
                                                ? `${apiUrl}/img/exact/bezienswaardigheden/middel/600/450/80/${item.images?.[0]}`
                                                : '/assets/images/placeholder.jpg'
                                        }
                                        alt={item.titel}
                                    />
                                    <div className="bekijeck-content">
                                        {item.mp3_file ? (
                                            <h3>
                                                {item.titel}{' '}
                                                <img src="/assets/images/icon/speaker.svg" width="20" alt="speaker" />
                                            </h3>
                                        ) : (
                                            <h3>{item.titel} </h3>
                                        )}

                                        <span>{data.location?.titel}</span>
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </div>
            {/* End Hotspots Area */}
            {/* Start Map Area */}
            <div className="map-area ptb-100" data-aos="fade-up" data-aos-duration={1000}>
                <div className="container">
                    <div className="map-title">
                        <h2>Bekijk alle hotspots van {data?.location?.titel}</h2>
                    </div>
                </div>
                <div className="container-fluid p-0">
                    <div className="map-img google-map">
                        <HotspotsMap hotspots={data?.hotspots} location={data?.location?.url} />
                        {/* <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d39039.87237666487!2d6.4661064283000345!3d52.2979992327874!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c7f7003ba66417%3A0xccc987190622c218!2sRijssen!5e0!3m2!1snl!2snl!4v1681824987574!5m2!1snl!2snl" /> */}
                    </div>
                </div>
            </div>
            {/* End Map Area */}
            {/* Start Location video Area */}
            <div className="location-video-area pb-100">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6" data-aos="fade-up" data-aos-duration={1000}>
                            <div className="location-content">
                                <div className="right-container mb-5">
                                    <button className="right-container-button w-323">
                                        <span className="short-text">
                                            <img src="/assets/images/icon/location.svg" id="icon-chat" alt="" />
                                        </span>
                                        <span className="long-text">Bekijk een andere plaats</span>
                                    </button>
                                </div>
                                <div className="text-end">
                                    <h4>Bekijk onze YouTube video</h4>
                                    <img
                                        className="location-shape"
                                        src="/assets/images/location-shape.svg"
                                        alt="location-shape"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6" data-aos="fade-up" data-aos-duration={1000}>
                            <div className="video-wrap">
                                <iframe
                                    src="https://www.youtube.com/embed/i89EnCl3o2g"
                                    title="YouTube video player"
                                    frameBorder={0}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen=""
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* End Location video Area */}
            <Footer />
        </>
    );
}
